import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppInsightsService } from './application-insights.service';
import { Store } from '@ngxs/store';
import { User } from '../models/user.model';
import { CustomerSelectors } from '@sales/modules/details/states/customer.selectors';
import { OrderSelectors } from '@sales/states/order/order.selectors';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { UserSelectors } from '../states/user/user.selectors';

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private router = inject(Router);
  private appInsightsService = inject(AppInsightsService);
  private store = inject(Store);

  private currentCustomerId: string | null = null;
  private currentOrderServiceId: string | null = null;
  private currentDealerId: string | null = null;
  private currentUserId: string | null = null;

  constructor() {
    this.registerTelemetryInitializer();
    this.trackPageViews();
    this.subscribeToUserContextChanges();
    this.subscribeToOrderChanges();
  }

  private subscribeToUserContextChanges() {
    this.store.select<User>(UserSelectors.getUser).subscribe(user => {
      this.currentUserId = user?.sub || null;
      if (!this.currentUserId || !this.currentDealerId) return;
      this.appInsightsService.setAuthenticatedUserContext(this.currentUserId, this.currentDealerId);
    });

    this.store.select(DealerStateSelectors.activeDealer).subscribe(dealer => {
      this.currentDealerId = dealer?.dealerId || null;
      if (!this.currentUserId || !this.currentDealerId) return;
      this.appInsightsService.setAuthenticatedUserContext(this.currentUserId, this.currentDealerId);
    });
  }

  private trackPageViews() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const pageName = this.derivePageNameFromUrl(event.urlAfterRedirects);
      this.appInsightsService.trackPage(pageName, event.urlAfterRedirects);
    });
  }

  private derivePageNameFromUrl(url: string): string {
    const segments = url.split('/').filter(segment => segment);
    return segments
      .map(segment => {
        if (!isNaN(+segment)) {
          return ':id';
        }
        return segment;
      })
      .join('/');
  }

  private subscribeToOrderChanges(): void {
    this.store.select(CustomerSelectors.customerId).subscribe(customerId => {
      this.currentCustomerId = customerId;
    });

    this.store.select(OrderSelectors.currentOrderServiceId).subscribe(orderServiceId => {
      this.currentOrderServiceId = orderServiceId;
    });
  }

  private registerTelemetryInitializer(): void {
    this.appInsightsService.addTelemetryInitializer(envelope => {
      const baseData = envelope.baseData;
      if (!baseData) return;

      baseData.properties = baseData.properties || {};

      if (this.currentCustomerId) {
        baseData.properties['customerId'] = this.currentCustomerId;
      }
      if (this.currentOrderServiceId) {
        baseData.properties['orderServiceId'] = this.currentOrderServiceId;
      }
      if (this.currentDealerId) {
        baseData.properties['dealerId'] = this.currentDealerId;
      }
    });
  }
}
